// frontend/src/components/layout/AppBottomNav.tsx
/**
 * Global mobile bottom navigation bar.
 * Visible only on authenticated app pages (lg:hidden).
 * Routes: /app (Calculator) | /app/steel-coil | Son 5 Hesap (Modal) | /surveys | /profile
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calculator, Disc3, History, FileText, User } from 'lucide-react';
import { hapticFeedback } from '../../utils/nativeBridge';
import { useLanguage } from '../../contexts/LanguageContext';
import { RecentCalculationsModal } from '../ui/RecentCalculationsModal';

interface NavItem {
    id: string;
    path?: string;
    icon: React.ReactNode;
    labelTR: string;
    labelEN: string;
    isAction?: boolean;
}

const NAV_ITEMS: NavItem[] = [
    {
        id: 'calculator',
        path: '/app',
        icon: <Calculator className="w-5 h-5" />,
        labelTR: 'Hesapla',
        labelEN: 'Calculate',
    },
    {
        id: 'steel-coil',
        path: '/app/steel-coil',
        icon: <Disc3 className="w-5 h-5" />,
        labelTR: 'Çelik Rulo',
        labelEN: 'Steel Coil',
    },
    {
        id: 'recent-calcs',
        icon: <History className="w-5 h-5" />,
        labelTR: 'Son 5 Hesap',
        labelEN: 'Recent',
        isAction: true,
    },
    {
        id: 'surveys',
        path: '/surveys',
        icon: <FileText className="w-5 h-5" />,
        labelTR: 'Raporlar',
        labelEN: 'Reports',
    },
    {
        id: 'profile',
        path: '/profile',
        icon: <User className="w-5 h-5" />,
        labelTR: 'Profil',
        labelEN: 'Profile',
    },
];

// Pages where the global bottom nav should appear
const VISIBLE_ON = ['/app', '/app/steel-coil', '/surveys', '/profile'];

export const AppBottomNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { lang } = useLanguage();
    const [isRecentModalOpen, setIsRecentModalOpen] = useState(false);

    // Only show on designated authenticated pages, only on mobile (lg:hidden handles this via CSS)
    const shouldShow = VISIBLE_ON.some(
        (p) => location.pathname === p || location.pathname.startsWith(p + '/')
    );
    if (!shouldShow) return null;

    const isActive = (item: NavItem): boolean => {
        if (item.isAction) return false;
        if (!item.path) return false;
        return location.pathname === item.path;
    };

    const handleItemClick = (item: NavItem) => {
        hapticFeedback('light');
        if (item.isAction && item.id === 'recent-calcs') {
            setIsRecentModalOpen(true);
        } else if (item.path) {
            navigate(item.path);
        }
    };

    return (
        <>
            <nav
                aria-label={lang === 'tr' ? 'Uygulama Navigasyonu' : 'App Navigation'}
                className="fixed bottom-0 left-0 right-0 z-[100] lg:hidden safe-bottom"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                {/* Blur backdrop */}
                <div className="absolute inset-0 bg-white/95 dark:bg-[#0D111C]/95 backdrop-blur-2xl border-t border-slate-200/80 dark:border-white/10 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]" />

                {/* Nav items */}
                <div className="relative flex items-stretch h-16 px-1.5">
                    {NAV_ITEMS.map((item) => {
                        const active = isActive(item);
                        return (
                            <button
                                key={item.id}
                                onClick={() => handleItemClick(item)}
                                aria-current={active ? 'page' : undefined}
                                className={`
                                    flex-1 flex flex-col items-center justify-center gap-0.5
                                    rounded-xl mx-0.5 my-1
                                    transition-all duration-200 active:scale-95
                                    ${active
                                        ? 'bg-orange-500/10 dark:bg-orange-500/15 text-orange-600 dark:text-orange-400 font-bold'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                    }
                                `}
                            >
                                <div className="relative">
                                    {item.icon}
                                    {active && (
                                        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                                    )}
                                </div>
                                <span className={`text-[10px] tracking-tight whitespace-nowrap ${active ? 'font-semibold' : 'font-medium'}`}>
                                    {lang === 'tr' ? item.labelTR : item.labelEN}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* Read-Only Recent Calculations Modal */}
            <RecentCalculationsModal
                isOpen={isRecentModalOpen}
                onClose={() => setIsRecentModalOpen(false)}
            />
        </>
    );
};

export default AppBottomNav;
